import { AdminOpsShell } from "./AdminOps";

function HomeSurface({ title, description, children, metrics }) {
  return (
    <section className="admin-ops-surface">
      <div className="admin-ops-surface-head">
        <div>
          <h4>{title}</h4>
          <p>{description}</p>
        </div>
        {metrics?.length ? (
          <div className="admin-ops-inline-metrics">
            {metrics.map((item) => (
              <span key={`${item.label}-${item.value}`} className="admin-ops-pill">
                {`${item.label} ${item.value}`}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function WorkbenchMetrics({ items }) {
  return (
    <section className="admin-ops-metric-grid">
      {items.map((item) => (
        <article key={item.title} className={`admin-ops-metric tone-${item.tone || "blue"}`}>
          <span>{item.title}</span>
          <strong>{item.value}</strong>
          <small>{item.detail}</small>
        </article>
      ))}
    </section>
  );
}

export default function AdminDashboardHomeWorkspaces({
  locale,
  texts,
  scopedPositionKey,
  scopedWorkspace,
  scopedWorkspaceMetrics,
  operationsChecklist,
  salesFollowUpQueue,
  hrTaskBoard,
  adminOverview,
  adminOrders,
  allowedTabs,
  openCreateProductEditor
}) {
  const isZh = locale === "zh";

  if (scopedPositionKey === "merchandising-procurement-specialist" && scopedWorkspace) {
    return (
      <AdminOpsShell
        eyebrow={isZh ? "岗位工作台" : "Role Workspace"}
        title={isZh ? "供应链工作台" : "Supply Chain Workbench"}
        description={isZh ? "把选品、采购、质检和发运节奏压进统一首页工作台，首页也使用完整壳层语言。" : "Bring merchandising, procurement, QA, and shipment rhythm into the same shell language used across the admin."}
        metrics={[
          { title: texts.productsCount, value: scopedWorkspaceMetrics.productCount || 0, detail: isZh ? "工作台商品总数" : "Products in workspace", tone: "blue" },
          { title: isZh ? "可推进商品" : "Launch Ready", value: scopedWorkspaceMetrics.readyForLaunchCount || 0, detail: isZh ? "已具备推进条件" : "Items ready to advance", tone: "emerald" },
          { title: isZh ? "采购待办" : "Procurement Queue", value: scopedWorkspaceMetrics.procurementQueueCount || 0, detail: isZh ? "待采购跟进项" : "Procurement items pending", tone: "amber" }
        ]}
      >
        <HomeSurface
          title={isZh ? "今日重点" : "Today Focus"}
          description={isZh ? "优先核对待选品池，推进已具备资料的商品进入采购，并同步在途节点。" : "Validate the merchandising queue, move ready products into procurement, and sync in-transit milestones."}
          metrics={[
            { label: isZh ? "在途发运" : "In Transit", value: scopedWorkspaceMetrics.inTransitCount || 0 }
          ]}
        >
          <p className="controlhub-merchandising-caption" style={{ margin: 0 }}>
            {isZh
              ? "首页工作台现在直接承接供应链日常动作，减少和内部运营页之间的视觉落差。"
              : "The home workspace now carries the same operational tone as the deeper supply-chain pages."}
          </p>
        </HomeSurface>
      </AdminOpsShell>
    );
  }

  if (scopedPositionKey === "operations-specialist" && scopedWorkspace) {
    return (
      <AdminOpsShell
        eyebrow={isZh ? "岗位工作台" : "Role Workspace"}
        title={isZh ? "运营工作台" : "Operations Workbench"}
        description={isZh ? "首页统一承接店铺结构、视觉素材、营销活动与经营数据。" : "Use the home workspace to review storefront structure, creative assets, campaign execution, and business performance."}
        toolbar={allowedTabs.includes("productCreate") ? (
          <div className="row-actions">
            <button type="button" className="controlhub-button" onClick={openCreateProductEditor}>
              {texts.openCreateProduct}
            </button>
          </div>
        ) : null}
        metrics={[
          { title: isZh ? "导航项" : "Nav Items", value: scopedWorkspaceMetrics.navItemCount || 0, detail: isZh ? "当前前台结构项" : "Current storefront navigation rows", tone: "blue" },
          { title: isZh ? "轮播图" : "Banners", value: scopedWorkspaceMetrics.bannerCount || 0, detail: isZh ? "当前首页视觉位" : "Homepage visual slots", tone: "violet" },
          { title: isZh ? "可售商品" : "Published Products", value: scopedWorkspaceMetrics.publishedProductCount || 0, detail: isZh ? "已发布商品数量" : "Published product count", tone: "emerald" }
        ]}
      >
        <HomeSurface
          title={isZh ? "发布检查" : "Publish Checklist"}
          description={isZh ? "把关键发布检查留在首页，减少在不同页面来回跳转。" : "Keep key publish checks on the home page to reduce context switching."}
        >
          <div className="admin-ops-placeholder">
            {operationsChecklist.map((item) => (
              <p key={`ops-check-${item.key}`}>{`${item.label}: ${item.value ? (isZh ? "已完成" : "Done") : (isZh ? "待完成" : "Pending")}`}</p>
            ))}
          </div>
        </HomeSurface>
      </AdminOpsShell>
    );
  }

  if (scopedPositionKey === "sales-executive" && scopedWorkspace) {
    return (
      <AdminOpsShell
        eyebrow={isZh ? "岗位工作台" : "Role Workspace"}
        title={isZh ? "业务工作台" : "Sales Workbench"}
        description={isZh ? "首页用统一壳层承接订单跟进、客户维护和成交推进。" : "Use the shared shell on home to track order follow-up, customer care, and conversion progress."}
        metrics={[
          { title: texts.ordersCount, value: scopedWorkspaceMetrics.orderCount || 0, detail: isZh ? "当前订单总量" : "Orders in workspace", tone: "blue" },
          { title: isZh ? "进行中订单" : "Active Orders", value: scopedWorkspaceMetrics.activeOrderCount || 0, detail: isZh ? "需要持续跟进" : "Orders requiring follow-up", tone: "amber" },
          { title: texts.customersCount, value: scopedWorkspaceMetrics.customerCount || 0, detail: isZh ? "当前客户总数" : "Managed customer count", tone: "emerald" }
        ]}
      >
        <HomeSurface
          title={isZh ? "待跟进订单" : "Follow-up Queue"}
          description={isZh ? "首页保留最需要今天处理的订单清单。" : "Keep the orders that need action today visible on the home page."}
          metrics={[
            { label: isZh ? "已交付" : "Delivered", value: scopedWorkspaceMetrics.deliveredOrderCount || 0 }
          ]}
        >
          <div className="admin-ops-placeholder">
            {salesFollowUpQueue.slice(0, 5).map((item) => (
              <p key={`sales-home-${item.id}`}>{`#${item.id} / ${item.status} / ${item.total} ${item.currency}`}</p>
            ))}
            {!salesFollowUpQueue.length ? <p>{texts.none}</p> : null}
          </div>
        </HomeSurface>
      </AdminOpsShell>
    );
  }

  if (scopedPositionKey === "hr-admin-specialist" && scopedWorkspace) {
    return (
      <AdminOpsShell
        eyebrow={isZh ? "岗位工作台" : "Role Workspace"}
        title={isZh ? "人力行政工作台" : "HR and Admin Workbench"}
        description={isZh ? "首页统一呈现人员状态、岗位覆盖和行政重点任务。" : "Use the home shell to review staff status, position coverage, and administrative priorities."}
        metrics={[
          { title: isZh ? "员工数" : "Staff", value: scopedWorkspaceMetrics.staffCount || 0, detail: isZh ? "当前纳入工作台的员工" : "Staff covered by this workspace", tone: "blue" },
          { title: isZh ? "启用中" : "Active", value: scopedWorkspaceMetrics.activeCount || 0, detail: isZh ? "当前启用账号" : "Currently active accounts", tone: "emerald" },
          { title: isZh ? "岗位数" : "Positions", value: scopedWorkspaceMetrics.positionCount || 0, detail: isZh ? "当前岗位模板数" : "Position templates maintained", tone: "violet" }
        ]}
      >
        <HomeSurface
          title={isZh ? "重点任务" : "Priority Tasks"}
          description={isZh ? "首页直接承接人力任务板和入岗阶段提示。" : "Keep the HR task board and onboarding stage cues directly on the home page."}
          metrics={[
            { label: isZh ? "受限账号" : "Restricted", value: scopedWorkspaceMetrics.restrictedCount || 0 }
          ]}
        >
          <div className="admin-ops-placeholder">
            {hrTaskBoard.slice(0, 5).map((item) => (
              <p key={`hr-home-${item.id}`}>{`${item.name || item.id} / ${item.jobTitle || "-"} / ${item.onboardingStage}`}</p>
            ))}
            {!hrTaskBoard.length ? <p>{texts.none}</p> : null}
          </div>
        </HomeSurface>
      </AdminOpsShell>
    );
  }

  return (
    <AdminOpsShell
      eyebrow={isZh ? "首页概览" : "Home Overview"}
      title={texts.hello}
      description={texts.supportLine}
      toolbar={(
        <div className="row-actions">
          <button type="button" className="controlhub-button" onClick={openCreateProductEditor}>
            {texts.addProduct}
          </button>
          <button type="button" className="controlhub-button ghost">
            {texts.importData}
          </button>
        </div>
      )}
      metrics={[
        { title: texts.revenue, value: adminOverview?.revenueUSD || 0, detail: isZh ? "当前营收概览" : "Current revenue snapshot", tone: "blue" },
        { title: texts.orderRecords, value: adminOrders.length, detail: isZh ? "当前订单记录数" : "Current order rows", tone: "violet" },
        { title: texts.customersCount, value: adminOverview?.customerCount || 0, detail: isZh ? "当前客户总数" : "Current customer count", tone: "emerald" }
      ]}
    >
      <section className="admin-ops-section-grid">
        <HomeSurface
          title={texts.addStoreName}
          description={texts.firstProductDesc}
        >
          <div className="row-actions">
            <button type="button" className="controlhub-button" onClick={openCreateProductEditor}>
              {texts.addProduct}
            </button>
            <button type="button" className="controlhub-button ghost">
              {texts.importData}
            </button>
          </div>
        </HomeSurface>

        <HomeSurface
          title={texts.customizeStore}
          description={texts.customizeStoreDesc}
        >
          <div className="row-actions">
            <button type="button" className="controlhub-button ghost">{texts.customTemplate}</button>
          </div>
        </HomeSurface>
      </section>

      <HomeSurface
        title={texts.quickSales}
        description={texts.quickSalesDesc}
        metrics={[
          { label: texts.lastRefresh, value: new Date().toLocaleTimeString() }
        ]}
      >
        <div className="admin-ops-inline-metrics">
          <span className="admin-ops-pill">{`${texts.payments} ${adminOverview?.revenueUSD || 0}`}</span>
          <span className="admin-ops-pill">{`${texts.shipping} ${adminOrders.length}`}</span>
          <span className="admin-ops-pill">{`${texts.domain} ${adminOverview?.customerCount || 0}`}</span>
        </div>
      </HomeSurface>
    </AdminOpsShell>
  );
}